import React, { useState, useEffect } from 'react';
import { userService } from '../../services/api';
import './UserProfile.css';

const UserProfile = ({ profile, onUpdate, initialTab = 'personal' }) => {
    const [activeTab, setActiveTab] = useState(initialTab);

    useEffect(() => {
        setActiveTab(initialTab);
    }, [initialTab]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [formData, setFormData] = useState({
        firstName: profile?.firstName || '',
        lastName: profile?.lastName || '',
        university: profile?.university || '',
        title: profile?.title || '',
        department: profile?.department || '',
        phone: profile?.phone || ''
    });

    const [pwdData, setPwdData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    useEffect(() => {
        if (profile) {
            setFormData({
                firstName: profile.firstName || '',
                lastName: profile.lastName || '',
                university: profile.university || '',
                title: profile.title || '',
                department: profile.department || '',
                phone: profile.phone || ''
            });
        }
    }, [profile]);

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handlePwdChange = (e) => {
        setPwdData({ ...pwdData, [e.target.name]: e.target.value });
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);
        try {
            const result = await userService.updateProfile(formData);
            if (onUpdate) onUpdate(result.user);
            setSuccess('Profile updated successfully.');
        } catch (err) {
            console.error('Update profile error:', err);
            setError(err.response?.data?.error || 'Failed to update profile.');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();

        if (pwdData.newPassword !== pwdData.confirmPassword) {
            setError('New passwords do not match.');
            return;
        }
        if (pwdData.newPassword.length < 6) {
            setError('New password must be at least 6 characters.');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://169.58.21.41/api';
            const token = localStorage.getItem('auth_token');
            const res = await fetch(`${API_URL}/users/change-password`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    currentPassword: pwdData.currentPassword,
                    newPassword: pwdData.newPassword
                })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Password change failed.');
            setPwdData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setSuccess('Password changed successfully.');
        } catch (err) {
            console.error('Change password error:', err);
            setError(err.message || 'Failed to change password.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="user-profile-container">
            <div className="profile-header-card">
                <div className="profile-avatar-large">
                    {profile?.firstName?.charAt(0)?.toUpperCase()}
                </div>
                <div className="profile-header-info">
                    <h2>{profile?.firstName} {profile?.lastName}</h2>
                    <span className="profile-email">{profile?.email}</span>
                    <div className="profile-badges">
                        <span className={`badge ${profile?.role?.toUpperCase() === 'ADMIN' ? 'admin' : 'user'}`}>
                            {profile?.role?.toUpperCase() === 'ADMIN' ? 'Admin' : 'User'}
                        </span>
                        <span className="badge status">Active</span>
                    </div>
                </div>
            </div>

            <div className="profile-content-wrapper">
                <div className="profile-tabs">
                    <button
                        className={`profile-tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
                        onClick={() => setActiveTab('personal')}
                    >
                        👤 Personal Info
                    </button>
                    <button
                        className={`profile-tab-btn ${activeTab === 'security' ? 'active' : ''}`}
                        onClick={() => setActiveTab('security')}
                    >
                        🔒 Security & Password
                    </button>
                </div>

                <div className="profile-tab-content">
                    {error && <div className="profile-alert error">{error}</div>}
                    {success && <div className="profile-alert success">{success}</div>}

                    {activeTab === 'personal' && (
                        <form onSubmit={handleUpdateProfile} className="profile-form">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>First Name</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleFormChange}
                                        placeholder="First name"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Last Name</label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleFormChange}
                                        placeholder="Last name"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>University / Institution</label>
                                    <input
                                        type="text"
                                        name="university"
                                        value={formData.university}
                                        onChange={handleFormChange}
                                        placeholder="University"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Title</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleFormChange}
                                        placeholder="e.g. Dr., Prof."
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Department</label>
                                    <input
                                        type="text"
                                        name="department"
                                        value={formData.department}
                                        onChange={handleFormChange}
                                        placeholder="Department"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Phone</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleFormChange}
                                        placeholder="Phone number"
                                    />
                                </div>
                            </div>
                            <button type="submit" className="btn-save" disabled={loading}>
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </form>
                    )}

                    {activeTab === 'security' && (
                        <form onSubmit={handleChangePassword} className="profile-form">
                            <div className="form-group single">
                                <label>Current Password</label>
                                <input
                                    type="password"
                                    name="currentPassword"
                                    value={pwdData.currentPassword}
                                    onChange={handlePwdChange}
                                    required
                                    placeholder="••••••••"
                                />
                            </div>
                            <div className="form-divider-text">Set New Password</div>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>New Password</label>
                                    <input
                                        type="password"
                                        name="newPassword"
                                        value={pwdData.newPassword}
                                        onChange={handlePwdChange}
                                        required
                                        placeholder="••••••••"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Confirm New Password</label>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        value={pwdData.confirmPassword}
                                        onChange={handlePwdChange}
                                        required
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>
                            <button type="submit" className="btn-save security" disabled={loading}>
                                {loading ? 'Updating...' : 'Update Password'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserProfile;
